import { useLocale } from "../hooks/useLocale";
import { msg } from "../locales/translate";
import { Badge } from "@ui/badge/Badge";
import { HBox, PanelBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Error } from "@ui/error/Error";
import { IconArrowDown } from "@ui/icons/IconArrowDown";
import { IconMastercard } from "@ui/icons/IconMastercard";
import { IconVisa } from "@ui/icons/IconVisa";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import { Typo } from "@ui/text/Typo";
import { Signal } from "badmfck-signal";
import { useEffect, useState } from "react";
import { IconApple } from "src/assets/terminal/icon_apple";
import { IconBank } from "src/assets/terminal/icon_bank";
import { IconCard } from "src/assets/terminal/icon_card";
import { IconGoogle } from "src/assets/terminal/icon_google";
import { GD } from "src/GD";
import { IClient } from "src/services/ClientService";
import { FormParser } from "./FormParser";
import { IProduct } from "./ProductFees";

export const ClientProducts = ({ client }: { client: IClient }) => {
	useLocale();
	const [data, setData] = useState<IAPIResponse | null>(null);
	//!TODO: show loading;
	const [_busy, setBusy] = useState(false);

	useEffect(() => {
		loadProducts();
	}, [client]);

	const loadProducts = async () => {
		setBusy(true);
		const response = await GD.REQ_API.request({
			method: "clients/product/list",
			data: {
				client_id: client.id,
			},
		});
		setBusy(false);
		setData(response);
	};

	const noData = data && data.data && !data.data.length;

	return (
		<VBox>
			<Error error={data?.error} />
			<Typo variant="headMedium">{msg("clientSProducts")}</Typo>
			<Typo variant="caption">{msg("availableProductsForTheSelectedClient")}</Typo>
			<Spacer variant="normal" />

			{noData && (
				<PanelBox variant="no-records" style={{ justifyContent: "center" }}>
					<Typo variant="medium" bold sx={{ textAlign: "center" }}>{msg("noProductsAddedYet")}<br />{msg("pleaseAddAtLeastOneProduct")}<Spacer variant="normal" />
						<Button
							variant="primarySmall"
							onClick={() => S_CLIENT_ADD_PRODUCT_FORM.invoke({ client: client, product: null, onSubmit: loadProducts })}>{msg("addProduct")}</Button>
					</Typo>
				</PanelBox>
			)}

			<VBox style={{ gap: "5px" }}>
				{data && data.data && data.data.length > 0 && data.data.map((product: any) => <ProductItem key={product.id} product={product} />)}
			</VBox>

			{!noData && (
				<>
					<Spacer variant="normal" />
					<HBox>
						<Spacer grow />
						<Button
							variant="primarySmall"
							onClick={() => S_CLIENT_ADD_PRODUCT_FORM.invoke({ client: client, product: null, onSubmit: loadProducts })}>{msg("addProduct")}</Button>
					</HBox>
				</>
			)}
			<ClientAddProductForm />
		</VBox>
	);
};

export const ProductItem = ({ opened, product, onExpand, onClick }: { product: IProduct; onClick?: () => void; opened?: boolean; onExpand?: () => void }) => {
	useLocale();
	let subtypeIcon = null;
	if (product.subtype === "visa") subtypeIcon = <IconVisa height={18} />;
	if (product.subtype === "mastercard") subtypeIcon = <IconMastercard height={18} />;
	if (product.subtype === "all")
		subtypeIcon = (
			<HBox center>
				<IconVisa height={18} />
				<Spacer size="4px" />
				<IconMastercard height={18} />
			</HBox>
		);

	let terminalIcon = null;
	if (product.terminal_type === "payment_card") terminalIcon = <IconCard size={"18px"} />;
	if (product.terminal_type === "apple_pay") terminalIcon = <IconApple size={"18px"} />;
	if (product.terminal_type === "google_pay") terminalIcon = <IconGoogle size={"18px"} />;
	if (product.terminal_type === "open_banking") terminalIcon = <IconBank size={"18px"} />;

	//const bgColor = opened ? "#200f2e" : undefined;
	const bgColor = opened ? "#e5e5e5" : undefined;
	const color = /*opened ? "white" :*/ undefined;

	return (
		<PanelBox variant="strict" key={product.id} style={{ backgroundColor: bgColor }}>
			<HBox variant="left-center">
				{terminalIcon}
				<Typo
					style={{
						cursor: onClick ? "pointer" : undefined,
						color: color,
						fontWeight: "bold",
					}}
					variant="medium"
					onClick={onClick}>
					{product.terminal_name}
				</Typo>
				{subtypeIcon}
				<Spacer grow />
				<Badge label={product.currency} variant="outline" />
				<Badge label={product.status} variant="outline" />
				{onExpand && <Button variant="icon" icon={<IconArrowDown rotated={opened || false} />} onClick={onExpand} />}
			</HBox>
		</PanelBox>
	);
};

//<Button variant="icon" icon={<IconArrowDown scheme="dark" rotated={opened} />} onClick={() => setOpened(!opened)} />

const S_CLIENT_ADD_PRODUCT_FORM = new Signal<{ client: IClient; product: any; onSubmit: () => void } | null>("S_CLIENT_ADD_PRODUCT_FORM");

const ClientAddProductForm = () => {
	useLocale();
	const client = S_CLIENT_ADD_PRODUCT_FORM.use();
	const [error, setError] = useState<IError | null>(null);
	useEffect(() => {
		if (client) {
			setError(null);
		}
	}, [client]);

	const [terminals] = GD.B_TERMINALS.use();

	if (client === null) return null;

	const form = {
		product: {
			terminal_id: "",
			$__terminal_id_type: "select",
			$__terminal_id_values: terminals?.data ? terminals.data.map((t: any) => ({ name: t.name, value: t.id })) : [],

			currency: "",
			$__currency_type: "select",
			$__currency_values: ["EUR", "USD"],

			subtype: "",
			$__subtype_type_linked: "terminal_id",
			$__subtype_type_linked_value: terminals?.data?.find((t: any) => t.type === "payment_card")?.id ?? -1,
			$__subtype_type: "select",
			$__subtype_values: ["all", "visa", "mastercard"],
		},
	};

	return (
		<Panel
			decorator="absolute"
			onClose={() => S_CLIENT_ADD_PRODUCT_FORM.invoke(null)}
			scrollable
			growContent
			label={msg("templateAddProductTo_1a83368d", { v0: client.client.name })}>
			<Error error={error} />
			<FormParser
				secondaryButton={null}
				onSubmit={async (data: any): Promise<any> => {
					// validate form
					const response = await GD.REQ_API.request({
						method: "clients/product/set",
						data: {
							client_id: client.client.id,
							terminal_id: (data as any).product.terminal_id,
							currency: (data as any).product.currency,
							subtype: !data.product.subtype ? null : data.product.subtype,
							id: client.product?.id, // if id exists - update, else - add
						},
					});
					if (response.error) setError(response.error);
					else {
						client.onSubmit();
						S_CLIENT_ADD_PRODUCT_FORM.invoke(null);
					}
					return data;
				}}
				onRequest={async (): Promise<unknown> => {
					return form;
				}}
			/>
		</Panel>
	);
};
